import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

type Toast = {
    id: string;
    message: string;
    type: ToastType;
};

type ToastContextValue = {
    showToast: (message: string, type?: ToastType) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const toastStyles: Record<ToastType, { icon: typeof CheckCircle2; className: string; iconClassName: string }> = {
    success: {
        icon: CheckCircle2,
        className: 'border-neon-primary/40 bg-black/80 text-white shadow-[0_10px_30px_rgba(29,78,255,0.2)]',
        iconClassName: 'text-neon-primary',
    },
    error: {
        icon: AlertTriangle,
        className: 'border-red-500/40 bg-black/80 text-white shadow-[0_10px_30px_rgba(239,68,68,0.2)]',
        iconClassName: 'text-red-400',
    },
    info: {
        icon: Info,
        className: 'border-blue-400/40 bg-black/80 text-white shadow-[0_10px_30px_rgba(96,165,250,0.2)]',
        iconClassName: 'text-blue-300',
    },
};

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const dismiss = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const showToast = useCallback((message: string, type: ToastType = 'success') => {
        const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        setToasts((prev) => [...prev, { id, message, type }]);
        // Errors stay longer (7s) so users can read; info/success at 5.5s
        // since 3s wasn't enough for non-power-users to read full text.
        const duration = type === 'error' ? 7000 : 5500;
        window.setTimeout(() => dismiss(id), duration);
    }, [dismiss]);

    const value = useMemo(() => ({ showToast }), [showToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div
                className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 pointer-events-none"
                aria-live="polite"
                aria-atomic="true"
            >
                {toasts.map((toast) => (
                    <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
    const { icon: Icon, className, iconClassName } = toastStyles[toast.type];

    return (
        <div
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border text-sm animate-in fade-in slide-in-from-bottom-2 duration-200 ${className}`}
            role="status"
        >
            <Icon size={16} className={iconClassName} />
            <span className="font-medium">{toast.message}</span>
            <button
                onClick={() => onDismiss(toast.id)}
                className="ml-2 text-white/60 hover:text-white transition-colors"
                aria-label="Fechar notificação"
            >
                <X size={14} />
            </button>
        </div>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
